/**
 * Admin / system strings (`adm.*`): users, roles, audit log, legal centre, help.
 * `admEn` is typed against `admPl`, so a missing key is a compile error.
 */
import { admUsersEn, admUsersPl } from './admin-users';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const admPl = {
  users: {
    ux: admUsersPl,
    subtitle: 'Konta użytkowników organizacji, role i przełożeni.',
    add: 'Nowy użytkownik',
    search: 'Szukaj po nazwisku lub e-mailu…',
    allRoles: 'Wszystkie role',
    allStatuses: 'Wszyscy',
    active: 'Aktywni',
    inactive: 'Nieaktywni',
    loadError: 'Nie udało się wczytać użytkowników.',
    empty: 'Brak użytkowników spełniających kryteria.',
    col: { user: 'Użytkownik', role: 'Rola', manager: 'Przełożony', status: 'Status', lastLogin: 'Ostatnie logowanie' },
    never: 'Nigdy',
    statusActive: 'Aktywny',
    statusInactive: 'Nieaktywny',
    twoFactor: '2FA',
    demo: 'Demo',
    edit: 'Edytuj',
    deactivate: 'Dezaktywuj',
    activate: 'Aktywuj',
    dialog: {
      createTitle: 'Nowy użytkownik',
      editTitle: 'Edycja użytkownika',
      firstName: 'Imię',
      lastName: 'Nazwisko',
      email: 'E-mail',
      phone: 'Telefon',
      role: 'Rola',
      manager: 'Przełożony',
      noManager: 'Brak',
      password: 'Hasło początkowe',
      passwordHint: 'Minimum 10 znaków. Użytkownik zmieni je po pierwszym logowaniu.',
      required: 'Uzupełnij wymagane pola.',
      emailInvalid: 'Podaj poprawny adres e-mail.',
      passwordShort: 'Hasło musi mieć co najmniej 10 znaków.',
      cancel: 'Anuluj',
      create: 'Utwórz',
      save: 'Zapisz',
      created: 'Użytkownik został utworzony.',
      updated: 'Zmiany zapisane.',
      saveError: 'Nie udało się zapisać. Sprawdź dane (e-mail może być już zajęty).',
    },
  },
  roles: {
    subtitle: 'Domyślna macierz uprawnień i zakres widoczności danych dla każdej roli.',
    readOnlyTitle: 'Tylko do odczytu',
    readOnlyBody: 'Edycja macierzy uprawnień będzie dostępna w kolejnym etapie. Zmiana roli użytkownika: Administracja → Użytkownicy.',
    permission: 'Uprawnienie',
    scope: 'Zakres danych',
    scopes: { OWN: 'Własne rekordy', TEAM: 'Zespół', ALL: 'Cała organizacja' },
    permissionsCount: '{count} uprawnień',
    users: '{count} użytkowników',
    groups: {
      customers: 'Klienci',
      leads: 'Leady',
      visits: 'Wizyty',
      tasks: 'Zadania',
      orders: 'Zamówienia',
      logistics: 'Logistyka',
      receivables: 'Należności',
      workflow: 'Procesy i akceptacje',
      targets: 'Targety',
      bi: 'BI i analityka',
      ai: 'AI',
      admin: 'Administracja',
      erp: 'ERP',
      mbr: 'Raport zarządu',
    },
  },
  audit: {
    subtitle: 'Kto, co i kiedy zmienił w systemie. Wpisy są tylko do odczytu.',
    allActions: 'Wszystkie akcje',
    allEntities: 'Wszystkie obiekty',
    entityPlaceholder: 'Typ obiektu (np. Customer)…',
    loadError: 'Nie udało się wczytać dziennika audytu.',
    empty: 'Brak wpisów audytu spełniających kryteria.',
    col: { when: 'Kiedy', who: 'Kto', action: 'Akcja', entity: 'Obiekt', ip: 'Adres IP' },
    system: 'System',
    changes: 'Zmiany',
    before: 'Przed',
    after: 'Po',
    noDetails: 'Brak szczegółów zmiany.',
    actions: {
      CREATE: 'Utworzenie',
      UPDATE: 'Zmiana',
      DELETE: 'Usunięcie',
      LOGIN: 'Logowanie',
      LOGIN_FAILED: 'Nieudane logowanie',
      LOGOUT: 'Wylogowanie',
      DEMO_SWITCH: 'Przełączenie konta demo',
      TWO_FACTOR_ENABLED: 'Włączenie 2FA',
      TWO_FACTOR_DISABLED: 'Wyłączenie 2FA',
      EXPORT: 'Eksport danych',
    },
  },
  legal: {
    subtitle: 'Dokumenty prawne obowiązujące w systemie NEXOPS.',
    notice: 'Dokumenty w wersji demonstracyjnej — ostateczne brzmienie zostanie ustalone z klientem.',
    version: 'Wersja {version}',
    updated: 'Obowiązuje od {date}',
    docs: {
      terms: { title: 'Regulamin korzystania', body: 'Zasady korzystania z platformy, odpowiedzialność użytkowników i administratora, zasady dostępu do kont.' },
      privacy: { title: 'Polityka prywatności', body: 'Jakie dane osobowe przetwarzamy, w jakim celu, na jakiej podstawie i jak długo je przechowujemy.' },
      gdpr: { title: 'Informacja RODO', body: 'Administrator danych, prawa osób, których dane dotyczą, kontakt w sprawach ochrony danych.' },
      dpa: { title: 'Umowa powierzenia danych', body: 'Warunki powierzenia przetwarzania danych osobowych pomiędzy klientem a dostawcą platformy.' },
      cookies: { title: 'Pliki cookie', body: 'System używa wyłącznie niezbędnych plików cookie sesji (httpOnly) — bez śledzenia i reklam.' },
      security: { title: 'Bezpieczeństwo informacji', body: 'Zakres danych egzekwowany po stronie serwera, dziennik audytu, uwierzytelnianie dwuskładnikowe.' },
    },
  },
  help: {
    subtitle: 'Szybki start, najczęstsze pytania i historia zmian.',
    tabs: { start: 'Szybki start', faq: 'Pytania i odpowiedzi', changelog: 'Co nowego' },
    startTitle: 'Od czego zacząć',
    steps: {
      myDay: { title: 'Mój dzień', body: 'Zacznij zmianę tutaj: zadania na dziś, alerty i wynik tygodnia w jednym miejscu.' },
      customers: { title: 'Klienci', body: 'Karta klienta 360°: sprzedaż, produkty, wizyty, dokumenty i alerty.' },
      tasks: { title: 'Zadania i wizyty', body: 'Planuj kontakty, rejestruj wizyty i zamykaj zadania z wynikiem.' },
      analytics: { title: 'Analiza', body: 'Dashboardy zarządu i zespołu, marża, należności, prognozy i premie.' },
    },
    faq: {
      q1: { q: 'Dlaczego nie widzę niektórych klientów?', a: 'Zakres danych zależy od roli: handlowiec widzi swoich klientów, kierownik — cały zespół, zarząd — wszystkich.' },
      q2: { q: 'Skąd pochodzą dane sprzedaży?', a: 'Z systemu ERP (Subiekt), importowane w trybie tylko do odczytu. Data ostatniej synchronizacji jest widoczna w nagłówkach widoków.' },
      q3: { q: 'Jak zmienić rolę lub dezaktywować konto?', a: 'Administracja → Użytkownicy. Zmiany są zapisywane w dzienniku audytu.' },
      q4: { q: 'Co oznaczają kolory alertów?', a: 'Czerwony — wymaga reakcji dziś, bursztynowy — ryzyko w najbliższych dniach, niebieski — informacja.' },
      q5: { q: 'Jak zgłosić błąd lub pomysł?', a: 'Pozycja „Zgłoszenia" w menu (wkrótce). Do tego czasu napisz do opiekuna wdrożenia.' },
      q6: { q: 'Jak wysłać ogłoszenie z prośbą o potwierdzenie?', a: 'Komunikacja → Powiadomienia → „Nowe powiadomienie". Wybierz odbiorców (wszyscy, rola, mój zespół lub konkretne osoby), wpisz treść i zaznacz kanały: Push, E-mail, „Wymagaj potwierdzenia", „Przypnij". Po wysłaniu w zakładce „Wysłane" widzisz, kto odczytał i kto potwierdził.' },
      q7: { q: 'Jak czytać pulpit Zarząd?', a: 'Kafle pokazują wynik okresu na tle poprzedniego i celu. Kliknięcie kafla lub wykresu otwiera szczegóły (drill-down) do regionów, handlowców i klientów, a filtry u góry (okres, region, zespół) zmieniają cały widok jednocześnie. Zakres danych zależy od Twojej roli.' },
      q8: { q: 'Czym różni się sprzedaż, marża i zysk po kosztach transportu?', a: 'Sprzedaż to przychód netto. Marża to sprzedaż minus koszt towaru. Zysk rzeczywisty to marża pomniejszona o koszty transportu i obsługi — kaskadę widać w Marża, a ranking klientów i tras po kosztach w Rentowności. Klient o wysokiej sprzedaży potrafi mieć niski zysk rzeczywisty.' },
      q9: { q: 'Jak szybko znaleźć klienta, produkt lub widok?', a: 'Naciśnij Ctrl+K (⌘K na Macu) albo kliknij wyszukiwarkę u góry. Wpisz nazwę, NIP, miasto lub nazwę widoku — wyniki obejmują tylko dane z Twojego zakresu.' },
      q10: { q: 'Jak wyeksportować raport do PDF lub Excela?', a: 'W widokach analitycznych użyj przycisku eksportu w nagłówku lub nad tabelą. Plik zawiera dane dokładnie tak, jak je widzisz (z aktywnymi filtrami) i w Twoim zakresie uprawnień.' },
      q11: { q: 'Jak korzystać z asystenta „Zapytaj NEXOPS"?', a: 'Otwórz go przyciskiem w prawym dolnym rogu lub skrótem Ctrl+J i zadaj pytanie o dane, np. „którzy klienci przestali zamawiać w tym miesiącu?". Asystent widzi tylko dane z Twojego zakresu. Ważne liczby zawsze zweryfikuj w widoku źródłowym przed podjęciem decyzji.' },
    },
    changelogTitle: 'Historia zmian',
    changelog: {
      v1: { version: 'Menu', date: '2026-10-08', items: 'Nowe menu: Mój dzień i Mapa na żywo na górze, kolorowe podsekcje Analizy, sekcje Prawo i System. Użytkownicy, Role, Audyt, Centrum prawne i Pomoc.' },
    },
    contactTitle: 'Potrzebujesz pomocy?',
    contactBody: 'Skontaktuj się z opiekunem wdrożenia lub administratorem systemu w Twojej organizacji.',
  },
};

export type AdmMessages = Shape<typeof admPl>;

export const admEn: AdmMessages = {
  users: {
    ux: admUsersEn,
    subtitle: 'Organization user accounts, roles and managers.',
    add: 'New user',
    search: 'Search by name or e-mail…',
    allRoles: 'All roles',
    allStatuses: 'Everyone',
    active: 'Active',
    inactive: 'Inactive',
    loadError: 'Could not load users.',
    empty: 'No users match the criteria.',
    col: { user: 'User', role: 'Role', manager: 'Manager', status: 'Status', lastLogin: 'Last login' },
    never: 'Never',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    twoFactor: '2FA',
    demo: 'Demo',
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Activate',
    dialog: {
      createTitle: 'New user',
      editTitle: 'Edit user',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'E-mail',
      phone: 'Phone',
      role: 'Role',
      manager: 'Manager',
      noManager: 'None',
      password: 'Initial password',
      passwordHint: 'At least 10 characters. The user should change it after the first login.',
      required: 'Fill in the required fields.',
      emailInvalid: 'Enter a valid e-mail address.',
      passwordShort: 'Password must be at least 10 characters.',
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
      created: 'User created.',
      updated: 'Changes saved.',
      saveError: 'Could not save. Check the data (the e-mail may already be taken).',
    },
  },
  roles: {
    subtitle: 'Default permission matrix and data visibility scope for each role.',
    readOnlyTitle: 'Read-only',
    readOnlyBody: 'Editing the permission matrix will arrive in a later stage. To change a user’s role: Administration → Users.',
    permission: 'Permission',
    scope: 'Data scope',
    scopes: { OWN: 'Own records', TEAM: 'Team', ALL: 'Whole organization' },
    permissionsCount: '{count} permissions',
    users: '{count} users',
    groups: {
      customers: 'Customers',
      leads: 'Leads',
      visits: 'Visits',
      tasks: 'Tasks',
      orders: 'Orders',
      logistics: 'Logistics',
      receivables: 'Receivables',
      workflow: 'Workflows & approvals',
      targets: 'Targets',
      bi: 'BI & analytics',
      ai: 'AI',
      admin: 'Administration',
      erp: 'ERP',
      mbr: 'Management report',
    },
  },
  audit: {
    subtitle: 'Who changed what and when. Entries are read-only.',
    allActions: 'All actions',
    allEntities: 'All objects',
    entityPlaceholder: 'Object type (e.g. Customer)…',
    loadError: 'Could not load the audit log.',
    empty: 'No audit entries match the criteria.',
    col: { when: 'When', who: 'Who', action: 'Action', entity: 'Object', ip: 'IP address' },
    system: 'System',
    changes: 'Changes',
    before: 'Before',
    after: 'After',
    noDetails: 'No change details.',
    actions: {
      CREATE: 'Created',
      UPDATE: 'Updated',
      DELETE: 'Deleted',
      LOGIN: 'Login',
      LOGIN_FAILED: 'Failed login',
      LOGOUT: 'Logout',
      DEMO_SWITCH: 'Demo account switch',
      TWO_FACTOR_ENABLED: '2FA enabled',
      TWO_FACTOR_DISABLED: '2FA disabled',
      EXPORT: 'Data export',
    },
  },
  legal: {
    subtitle: 'Legal documents in force in NEXOPS.',
    notice: 'Documents are demo versions — final wording will be agreed with the customer.',
    version: 'Version {version}',
    updated: 'In force since {date}',
    docs: {
      terms: { title: 'Terms of use', body: 'Rules of using the platform, responsibilities of users and the administrator, account access rules.' },
      privacy: { title: 'Privacy policy', body: 'What personal data we process, why, on what basis and for how long we keep it.' },
      gdpr: { title: 'GDPR information', body: 'Data controller, data subject rights, contact for data protection matters.' },
      dpa: { title: 'Data processing agreement', body: 'Terms of entrusting personal data processing between the customer and the platform provider.' },
      cookies: { title: 'Cookies', body: 'The system uses only essential session cookies (httpOnly) — no tracking, no ads.' },
      security: { title: 'Information security', body: 'Server-side data scoping, audit log, two-factor authentication.' },
    },
  },
  help: {
    subtitle: 'Quick start, frequently asked questions and release notes.',
    tabs: { start: 'Quick start', faq: 'FAQ', changelog: 'What’s new' },
    startTitle: 'Where to start',
    steps: {
      myDay: { title: 'My day', body: 'Start your shift here: today’s tasks, alerts and the weekly result in one place.' },
      customers: { title: 'Customers', body: '360° customer card: sales, products, visits, documents and alerts.' },
      tasks: { title: 'Tasks and visits', body: 'Plan contacts, log visits and close tasks with an outcome.' },
      analytics: { title: 'Analytics', body: 'Board and team dashboards, margin, receivables, forecasts and bonuses.' },
    },
    faq: {
      q1: { q: 'Why can’t I see some customers?', a: 'Data scope depends on the role: a sales rep sees their own customers, a manager the whole team, the board everyone.' },
      q2: { q: 'Where does sales data come from?', a: 'From the ERP (Subiekt), imported read-only. The last sync time is shown in view headers.' },
      q3: { q: 'How do I change a role or deactivate an account?', a: 'Administration → Users. Changes are recorded in the audit log.' },
      q4: { q: 'What do the alert colours mean?', a: 'Red — needs action today, amber — risk in the coming days, blue — information.' },
      q5: { q: 'How do I report a bug or an idea?', a: 'The “Feedback” menu item (coming soon). Until then, contact your implementation lead.' },
      q6: { q: 'How do I send an announcement that needs confirmation?', a: 'Communication → Notifications → “New notification”. Choose recipients (everyone, a role, my team or specific people), write the text and tick the channels: Push, E-mail, “Require confirmation”, “Pin”. After sending, the “Sent” tab shows who read it and who confirmed.' },
      q7: { q: 'How do I read the Board dashboard?', a: 'Tiles show the period result against the previous period and the target. Clicking a tile or chart opens the detail (drill-down) down to regions, sales reps and customers, and the filters at the top (period, region, team) change the whole view at once. The data scope depends on your role.' },
      q8: { q: 'How do sales, margin and profit after transport costs differ?', a: 'Sales is net revenue. Margin is sales minus cost of goods. Real profit is margin reduced by transport and service costs — the cascade is in Margin, and the ranking of customers and routes after costs is in Profitability. A customer with high sales can have a low real profit.' },
      q9: { q: 'How do I quickly find a customer, product or view?', a: 'Press Ctrl+K (⌘K on Mac) or click the search at the top. Type a name, tax ID, city or view name — results only include data within your scope.' },
      q10: { q: 'How do I export a report to PDF or Excel?', a: 'In analytical views use the export button in the header or above the table. The file contains the data exactly as you see it (with active filters) and within your permission scope.' },
      q11: { q: 'How do I use the “Ask NEXOPS” assistant?', a: 'Open it with the button in the bottom-right corner or the Ctrl+J shortcut and ask about the data, e.g. “which customers stopped ordering this month?”. The assistant only sees data within your scope. Always verify important numbers in the source view before making a decision.' },
    },
    changelogTitle: 'Release notes',
    changelog: {
      v1: { version: 'Menu', date: '2026-10-08', items: 'New menu: My day and Live map on top, coloured Analytics subsections, Legal and System sections. Users, Roles, Audit, Legal centre and Help.' },
    },
    contactTitle: 'Need help?',
    contactBody: 'Contact your implementation lead or your organization’s system administrator.',
  },
};
